import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { PageContainer } from '@ge/components/containers';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import { useSiteList } from '@ge/feat-monitor/data-hooks';
import { MobileTab, MobileAssetTab } from '@ge/models/constants';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { killEventPropagation } from '@ge/shared/util/general';
import { globalColors } from '@ge/tokens/colors';

import AssetDetails from '../../components/list/asset-detail';
import AssetMobileList from '../../components/list/asset-row-list';
import SitesMobileList from '../../components/list/site-row-list';
import SiteDetailCard from '../../components/site-detail-card';
import SiteSearch from '../../components/site-search';
import { Tabs } from '../../components/tab-button';

// TODO: pull this from config, ideally something that can be updated without a redeploy
const TabContent = styled.div`
  display: ${(props) => (props.active ? 'block' : 'none')};
  .sites {
    display: flex;
    justify-content: space-between;
    margin: 2px 10px;
    p {
      color: ${globalColors.grey12};
    }
  }
  .status-badge {
    width: 25px;
    height: 4px;
    margin-top: 18px;
    @media (max-width: 364px) {
      width: 25px;
    }
  }
  .sitesList {
    display: flex;
    grid-template-columns: repeat(4, 2fr);
    rid-template-columns: 30px 50px;
    gap: 6px 12px;
    padding-left: 8px;
  }
`;

const Sites = (props) => {
  const {
    location: { search },
  } = props;
  /**
   * Set list of view tyoes for Presentation Toggle
   */
  const {
    entityId,
    showSiteDetails,
    hideDetails,
    showAssetDetails,
    resetDetails,
    isActive,
    setActiveState,
    entityType,
    entityTab,
  } = useContext(EntityDetailsContext);
  const { t, ready } = useTranslation(['monitor.sites', 'general'], {
    useSuspense: false,
  });

  const containerRef = useRef(null);
  const history = useHistory();

  const [loader, setLoader] = useState(true);
  const [activeTab, setActiveTab] = useState(MobileAssetTab.LIVE);
  useEffect(() => {
    if (loader) {
      const timer = setTimeout(() => {
        setLoader(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loader]);

  const defaultHistoryBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const [filterText, setFilterText] = useState('');
  const close = useCallback(() => {
    setLoader(true);
    resetDetails();
    hideDetails();
  }, [hideDetails, resetDetails]);

  const handleSiteTabChange = useCallback(
    (e, tab) => {
      killEventPropagation(e);
      close();
      setFilterText('');
      setActiveState(tab);
    },
    [close, setActiveState],
  );

  const handleAssetTabChange = useCallback(
    (e, tab) => {
      killEventPropagation(e);
      setFilterText('');
      defaultHistoryBack();
      setActiveState(tab);
    },
    [defaultHistoryBack, setActiveState],
  );

  useEffect(() => {
    // clear any filters
    setFilterText('');
  }, [setFilterText, isActive]);

  const [getSiteList, getFilteredAssets] = useSiteList({ filterText });

  const handleSiteRowSelect = useCallback(
    (e, siteId, tab) => {
      killEventPropagation(e);
      setActiveState(tab);
      setFilterText('');
      showSiteDetails(siteId);
    },
    [showSiteDetails, setActiveState],
  );

  const handleAssetClick = useCallback(
    (e, asset, tab) => {
      killEventPropagation(e);
      setFilterText('');
      showAssetDetails(asset.id);
      setActiveState(tab);
    },
    [showAssetDetails, setActiveState],
  );
  // Process URL params for setting initial view type.
  useEffect(() => {
    if (search) {
      // setLoader(true);
      setActiveState(isActive);
    } else {
      // setLoader(true);
      setActiveState(MobileTab.SITE);
    }
  }, [search, setActiveState, isActive]);

  const handleFilterTextChange = useCallback((event) => {
    event.preventDefault();
    killEventPropagation(event);
    const value = event.target.value.toLowerCase();
    setFilterText(value);
  }, []);

  return (
    <PageContainer i18nReady={ready} ref={containerRef}>
      <Tabs
        onChangeHandler={handleSiteTabChange}
        active={isActive}
        translateFn={t}
        i18nReady={ready}
      />
      <TabContent active={isActive === MobileTab.SITE}>
        <SiteSearch
          onChangeHandler={handleFilterTextChange}
          filterText={filterText}
          placeholder={t('general:search_site', 'Search Sites')}
          translateFn={t}
        />
      </TabContent>
      <TabContent active={isActive === MobileTab.ASSET}>
        <SiteDetailCard
          siteId={entityId}
          assetId={entityId}
          handleHistoryBack={handleSiteTabChange}
          translateFn={t} />
        <SiteSearch
          onChangeHandler={handleFilterTextChange}
          filterText={filterText}
          placeholder={t('general:search_turbine', 'Search Turbines')}
          isAsset
        />
      </TabContent>
      <TabContent active={isActive === MobileTab.TURBINE}>
        <SiteDetailCard
          assetId={entityId}
          handleHistoryBack={handleAssetTabChange}
          isTurbine={isActive === MobileTab.TURBINE}
          translateFn={t}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </TabContent>
      <ScrollingContainer>
        <SitesMobileList
          getFilteredSites={getSiteList}
          filterText={filterText}
          onRowSelect={handleSiteRowSelect}
          isLoading={filterText}
          active={isActive === MobileTab.SITE}
          siteLoader={loader}
        />
        <AssetMobileList
          getFilteredAssets={getFilteredAssets}
          filterText={filterText}
          onRowSelect={handleAssetClick}
          siteId={entityId}
          active={isActive === MobileTab.ASSET}
        />
        <AssetDetails
          assetId={entityId}
          entityType={entityType}
          entityTab={entityTab}
          active={isActive === MobileTab.TURBINE}
          activeTab={activeTab}
          siteId={entityId}
        />
      </ScrollingContainer>
    </PageContainer>
  );
};

Sites.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
    body: PropTypes.instanceOf(Object),
  }),
};

Sites.defaultProps = {
  location: null,
};

export default withRouter(Sites);
